import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import api from "../lib/api";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, Download, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";

export default function AccionesBulk() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const downloadTemplate = async () => {
    setDownloading(true);
    try {
      const { data } = await api.get("/acciones/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla_acciones_vincularedu.xlsx";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("bulk_x.downloaded"));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bulk_x.download_err"));
    } finally {
      setDownloading(false);
    }
  };

  const runPreview = async (f) => {
    setLoadingPreview(true);
    setPreview(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("excel", f);
      const { data } = await api.post("/acciones/bulk-preview", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
      if (data.invalid_count === 0) {
        toast.success(t("bulk_x.ready", { n: data.valid_count }));
      } else {
        toast.error(t("bulk_x.invalid", { n: data.invalid_count }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bulk_x.validate_err"));
      setFile(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runPreview(f);
  };

  const resetAll = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
  };

  const confirmImport = async () => {
    if (!file || !preview) return;
    setLoadingSubmit(true);
    try {
      const fd = new FormData();
      fd.append("excel", file);
      const { data } = await api.post("/acciones/bulk-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(data);
      toast.success(t("bulk_x.done", { n: data.inserted }));
    } catch (err) {
      toast.error(err?.response?.data?.detail || t("bulk_x.process_err"));
    } finally {
      setLoadingSubmit(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 lg:px-12 py-8 sm:py-12">
        <div className="mb-8 sm:mb-10">
          <div className="section-marker">{t("bulk.kicker")}</div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tighter mt-2">
            {t("bulk_x.title_a")} <span className="serif-em">{t("bulk_x.title_em")}</span>
          </h1>
          <p className="mt-3 text-zinc-600 max-w-2xl">{t("bulk.desc")}</p>
        </div>

        {/* Descargar plantilla */}
        <div className="border-2 border-zinc-950 p-5 sm:p-6 mb-6 bg-white flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6" data-testid="template-card">
          <div className="flex-1 min-w-0">
            <div className="section-marker mb-2">{t("bulk.template_kicker")}</div>
            <div className="font-medium text-lg">{t("bulk.template_title")}</div>
            <p className="text-sm text-zinc-600 mt-1">{t("bulk.template_desc")}</p>
          </div>
          <button
            type="button"
            onClick={downloadTemplate}
            disabled={downloading}
            className="bg-zinc-950 text-white px-5 py-3 font-medium hover:bg-zinc-800 inline-flex items-center justify-center gap-2 disabled:opacity-50 shrink-0 w-full sm:w-auto"
            data-testid="btn-download-template"
          >
            <Download size={18} strokeWidth={1.5} />
            {downloading ? t("bulk.template_generating") : t("bulk.template_btn")}
          </button>
        </div>

        {/* Dropzone */}
        {!file && (
          <label
            className="border-2 border-dashed border-zinc-300 p-6 sm:p-10 flex flex-col items-center justify-center cursor-pointer hover:border-zinc-950 transition-colors min-h-[200px] text-center"
            data-testid="bulk-dropzone"
          >
            <FileSpreadsheet size={48} strokeWidth={1} className="text-zinc-400 mb-3" />
            <div className="font-medium">{t("bulk.dropzone")}</div>
            <div className="text-sm text-zinc-500 mt-1">{t("bulk.dropzone_hint")}</div>
            <input
              type="file"
              accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              onChange={onFileChange}
              className="hidden"
              data-testid="bulk-input"
            />
          </label>
        )}

        {/* File + preview header */}
        {file && (
          <div className="border border-zinc-200 p-5 mb-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="file-header">
            <FileSpreadsheet size={32} strokeWidth={1} className="text-zinc-700 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{file.name}</div>
              <div className="text-xs text-zinc-500">{(file.size / 1024).toFixed(0)} KB</div>
            </div>
            <button
              onClick={resetAll}
              className="text-sm border border-zinc-300 px-4 py-2 hover:bg-zinc-50 inline-flex items-center gap-2"
              data-testid="btn-reset"
            >
              <RefreshCw size={14} strokeWidth={1.5} />
              {t("bulk.change_file")}
            </button>
          </div>
        )}

        {/* Loading preview */}
        {loadingPreview && (
          <div className="border border-zinc-200 p-10 text-center text-zinc-500" data-testid="loading-preview">
            {t("bulk.validating")}
          </div>
        )}

        {/* Preview result */}
        {preview && !result && (
          <>
            {/* Summary stats */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6" data-testid="preview-stats">
              <div className="border-2 border-emerald-700 p-5 bg-white">
                <div className="section-marker text-emerald-700 flex items-center gap-1">
                  <CheckCircle2 size={12} strokeWidth={2} /> {t("bulk.valid_stat")}
                </div>
                <div className="kpi-number text-5xl text-emerald-700 mt-2" data-testid="preview-valid-count">
                  {preview.valid_count}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{t("bulk.valid_rows")}</div>
              </div>
              <div className={`border-2 p-5 bg-white ${preview.invalid_count > 0 ? "border-red-700" : "border-zinc-200"}`}>
                <div className={`section-marker flex items-center gap-1 ${preview.invalid_count > 0 ? "text-red-700" : ""}`}>
                  <AlertCircle size={12} strokeWidth={2} /> {t("bulk.invalid_stat")}
                </div>
                <div
                  className={`kpi-number text-5xl mt-2 ${preview.invalid_count > 0 ? "text-red-700" : "text-zinc-400"}`}
                  data-testid="preview-invalid-count"
                >
                  {preview.invalid_count}
                </div>
                <div className="text-xs text-zinc-500 mt-1">{t("bulk.invalid_rows")}</div>
              </div>
              <div className="border border-zinc-200 p-5 bg-white">
                <div className="section-marker">{t("bulk.total_stat")}</div>
                <div className="kpi-number text-5xl text-zinc-950 mt-2">{preview.total_rows}</div>
                <div className="text-xs text-zinc-500 mt-1">{t("bulk.total_rows")}</div>
              </div>
            </div>

            {/* Errors table */}
            {preview.invalid_count > 0 && (
              <div className="border border-red-200 bg-red-50/30 mb-6" data-testid="errors-table">
                <div className="p-4 border-b border-red-200">
                  <div className="font-medium text-red-900 flex items-center gap-2">
                    <AlertCircle size={16} strokeWidth={1.5} />
                    {t("bulk.errors_title")}
                  </div>
                  <div className="text-xs text-zinc-600 mt-1">{t("bulk.errors_hint")}</div>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[600px] text-sm">
                    <thead>
                      <tr className="border-b border-red-200 bg-white/60">
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-16">{t("bulk.col_row")}</th>
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_error")}</th>
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_data")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.invalid.map((e, i) => (
                        <tr key={i} className="border-b border-red-100 last:border-0" data-testid={`error-row-${e.row}`}>
                          <td className="px-4 py-3 tabular-nums font-medium">{e.row}</td>
                          <td className="px-4 py-3 text-red-800">{e.error}</td>
                          <td className="px-4 py-3 text-xs text-zinc-600 font-mono">
                            {e.data?.convenio_id && <div>conv: <span className="text-zinc-400">{e.data.convenio_id.slice(0, 18)}…</span></div>}
                            {e.data?.tipo && <div>tipo: {e.data.tipo}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Valid rows preview */}
            {preview.valid_count > 0 && (
              <div className="border border-emerald-200 bg-emerald-50/30 mb-6" data-testid="valid-table">
                <div className="p-4 border-b border-emerald-200">
                  <div className="font-medium text-emerald-900 flex items-center gap-2">
                    <CheckCircle2 size={16} strokeWidth={1.5} />
                    {t("bulk.valid_title")}
                  </div>
                </div>
                <div className="overflow-x-auto max-h-80">
                  <table className="w-full min-w-[760px] text-sm">
                    <thead className="bg-white/60 sticky top-0">
                      <tr className="border-b border-emerald-200">
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500 w-14">{t("bulk.col_row")}</th>
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_institucion")}</th>
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_tipo")}</th>
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_facultad")}</th>
                        <th className="text-left px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_period")}</th>
                        <th className="text-right px-4 py-2 text-xs font-bold uppercase tracking-wider text-zinc-500">{t("bulk.col_part")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.valid.map((v) => (
                        <tr key={v.row} className="border-b border-emerald-100 last:border-0">
                          <td className="px-4 py-2 tabular-nums">{v.row}</td>
                          <td className="px-4 py-2 font-medium">{v.institucion_nombre}</td>
                          <td className="px-4 py-2 text-zinc-600">{v.tipo}</td>
                          <td className="px-4 py-2 text-zinc-600">{v.facultad_nombre}</td>
                          <td className="px-4 py-2 text-zinc-600 tabular-nums">{v.semestre}/{v.anio}</td>
                          <td className="px-4 py-2 text-right tabular-nums font-medium">{v.num_participantes}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Confirm bar */}
            <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t border-zinc-200 pt-6">
              <button
                onClick={() => nav(-1)}
                className="border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50 w-full sm:w-auto"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={confirmImport}
                disabled={preview.valid_count === 0 || loadingSubmit}
                className="flex-1 bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
                data-testid="btn-confirm-import"
              >
                <Upload size={18} strokeWidth={1.5} />
                {loadingSubmit
                  ? t("bulk.processing")
                  : preview.valid_count === 0
                  ? t("bulk.no_valid")
                  : t("bulk.confirm_btn", { n: preview.valid_count })}
              </button>
            </div>
          </>
        )}

        {/* Final result */}
        {result && (
          <div className="border-2 border-emerald-700 bg-white p-6 sm:p-8" data-testid="bulk-result">
            <div className="section-marker text-emerald-700 flex items-center gap-1">
              <CheckCircle2 size={12} strokeWidth={2} /> {t("bulk.done_kicker")}
            </div>
            <h2 className="text-3xl font-medium tracking-tight mt-2">
              {t("bulk.done_title", { n: result.inserted })} <span className="serif-em">{t("bulk.done_title_em")}</span>
            </h2>
            <div className="mt-6 flex flex-wrap items-baseline gap-6">
              <div>
                <div className="kpi-number text-4xl sm:text-5xl text-emerald-700">{result.inserted}</div>
                <div className="section-marker mt-1">{t("bulk.inserted")}</div>
              </div>
              <div>
                <div className="kpi-number text-4xl sm:text-5xl text-zinc-400">{result.total_rows}</div>
                <div className="section-marker mt-1">{t("bulk_x.total_filas")}</div>
              </div>
              {result.errors.length > 0 && (
                <div>
                  <div className="kpi-number text-4xl sm:text-5xl text-red-700">{result.errors.length}</div>
                  <div className="section-marker mt-1">{t("bulk.rejected")}</div>
                </div>
              )}
            </div>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => nav("/acciones")}
                className="bg-zinc-950 text-white px-6 py-3 font-medium hover:bg-zinc-800"
                data-testid="btn-see-acciones"
              >
                {t("bulk.see_actions")}
              </button>
              <button
                onClick={resetAll}
                className="border border-zinc-300 px-6 py-3 font-medium hover:bg-zinc-50"
                data-testid="btn-new-upload"
              >
                {t("bulk.new_upload")}
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
